import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, Clock, Package, MessageSquare, Star, XCircle, Filter, CheckCircle, MoreVertical, ChevronRight } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import { getUserNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../../services/api';
import MobileBottomNav from '../../components/layout/MobileBottomNav';

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [user, navigate]);

  const fetchNotifications = async () => {
    try {
      const response = await getUserNotifications();
      if (response.success) {
        setNotifications(response.data || []);
      }
    } catch (error) {
      // console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <Package className="w-5 h-5 text-blue-500" />;
      case 'message':
        return <MessageSquare className="w-5 h-5 text-green-500" />;
      case 'rating':
        return <Star className="w-5 h-5 text-yellow-500" />;
      case 'system':
        return <Bell className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await markNotificationAsRead(notificationId, user.user_id);
      if (response.success) {
        setNotifications(notifications.map(notif => 
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        ));
        setShowActionsMenu(null);
      }
    } catch (error) {
      // console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    try {
      const response = await markAllNotificationsAsRead(user.user_id);
      if (response.success) {
        setNotifications(notifications.map(notif => ({ ...notif, is_read: true })));
      }
    } catch (error) {
      // console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await deleteNotification(notificationId, user.user_id);
      if (response.success) {
        setNotifications(notifications.filter(notif => notif.id !== notificationId));
        setSelectedNotifications(selectedNotifications.filter(id => id !== notificationId));
        setShowActionsMenu(null);
      }
    } catch (error) {
      // console.error('Error deleting notification:', error);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      for (const notificationId of selectedNotifications) {
        await deleteNotification(notificationId, user.user_id);
      }
      setNotifications(notifications.filter(notif => !selectedNotifications.includes(notif.id)));
      setSelectedNotifications([]);
      setSelectAll(false);
    } catch (error) {
      // console.error('Error deleting selected notifications:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    // Toggle expansion on mobile
    if (window.innerWidth < 768) {
      setExpandedNotification(expandedNotification === notification.id ? null : notification.id);
      return;
    }
    
    // Navigate based on notification type (only on desktop)
    switch (notification.type) {
      case 'order':
        if (notification.related_id) {
          navigate(`/orders/${notification.related_id}`);
        }
        break;
      case 'message':
        if (notification.related_id) {
          navigate(`/messages/${notification.related_id}`);
        } else {
          navigate('/messages');
        }
        break;
      default:
        break;
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      const filtered = filteredNotifications.map(notif => notif.id);
      setSelectedNotifications(filtered);
    }
    setSelectAll(!selectAll);
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    if (filter === 'read') return notification.is_read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <ChevronRight className="w-5 h-5 text-black transform rotate-180" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Notifications</h1>
            
            {unreadCount > 0 && (
              <div className="w-10 flex items-center justify-end">
                <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {unreadCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Desktop Header */}
        <div className="hidden lg:block mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 mt-2">
                {unreadCount > 0 
                  ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                  : 'All caught up!'
                }
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {selectedNotifications.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedNotifications.length})
                </button>
              )}
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllNotificationsAsRead}
                  className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark All as Read
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Actions Bar */}
        <div className="lg:hidden mb-4">
          <div className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-600">Select all</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {selectedNotifications.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="p-2 text-red-600"
                  title="Delete selected"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllNotificationsAsRead}
                  className="p-2 text-blue-600"
                  title="Mark all as read"
                >
                  <Check className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters - Mobile */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Filter className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-gray-700 font-medium text-sm">Filter:</span>
            </div>
            <div className="flex space-x-1">
              {['all', 'unread', 'read'].map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                    filter === filterType
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterType}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters - Desktop */}
        <div className="hidden lg:block bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Filter className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700 font-medium">Filter:</span>
              </div>
              <div className="flex space-x-2">
                {['all', 'unread', 'read'].map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setFilter(filterType)}
                    className={`px-4 py-2 rounded-lg font-medium capitalize ${
                      filter === filterType
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filterType}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-600">Select all</span>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-xl shadow-sm border transition-all ${
                  !notification.is_read ? 'border-l-4 border-l-orange-500' : ''
                } ${expandedNotification === notification.id ? 'shadow-md' : ''}`}
              >
                <div className="p-4">
                  <div className="flex items-start">
                    {/* Selection Checkbox - Desktop */}
                    <div className="hidden lg:block mr-3 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification.id)}
                        onChange={() => handleSelectNotification(notification.id)}
                        className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                      />
                    </div>

                    {/* Notification Icon */}
                    <div className="mr-3 lg:mr-4">
                      <div className={`p-2 lg:p-3 rounded-full ${
                        !notification.is_read ? 'bg-orange-100' : 'bg-gray-100'
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>

                    {/* Notification Content */}
                    <div 
                      className="flex-1 cursor-pointer min-w-0"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <h3 className={`font-medium text-sm lg:text-base truncate ${
                              !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h3>
                            <span className="text-xs lg:text-sm text-gray-500 whitespace-nowrap ml-2">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 text-sm lg:text-base mt-1 line-clamp-20">
                            {notification.message}
                          </p>
                          
                          {(expandedNotification === notification.id || window.innerWidth >= 768) && 
                           notification.metadata && notification.metadata.details && (
                            <div className="mt-2 text-xs lg:text-sm text-gray-500 bg-gray-50 p-2 rounded">
                              <p>{notification.metadata.details}</p>
                            </div>
                          )}
                        </div>

                        {/* Read Status Dot */}
                        <div className="flex items-center mt-2 lg:mt-0 lg:ml-4">
                          {!notification.is_read && (
                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2 lg:mr-0"></span>
                          )}
                          
                          {/* Mobile Expand/Collapse Icon */}
                          <div className="lg:hidden ml-auto">
                            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                              expandedNotification === notification.id ? 'transform rotate-90' : ''
                            }`} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions - Desktop */}
                    <div className="hidden lg:flex flex-col space-y-2 ml-4">
                      {!notification.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Mobile Actions Menu */}
                    <div className="lg:hidden relative ml-2">
                      <button
                        onClick={() => setShowActionsMenu(showActionsMenu === notification.id ? null : notification.id)}
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      {showActionsMenu === notification.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-10">
                          <div className="py-1">
                            {!notification.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Check className="w-4 h-4 mr-3" />
                                Mark as read
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selection Checkbox - Mobile */}
                  <div className="lg:hidden flex items-center mt-3 pt-3 border-t">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">Select</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-8 lg:p-12 text-center">
              <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-4 lg:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Bell className="w-8 h-8 lg:w-12 lg:h-12 text-gray-400" />
              </div>
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                No notifications
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm lg:text-base">
                {filter === 'all'
                  ? "You're all caught up! No notifications at the moment."
                  : filter === 'unread'
                  ? 'No unread notifications. Great job keeping up!'
                  : 'No read notifications found.'
                }
              </p>
              {filter !== 'all' && (
                <button
                  onClick={() => setFilter('all')}
                  className="px-4 lg:px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm lg:text-base"
                >
                  View All Notifications
                </button>
              )}
            </div>
          )}
        </div>

        {/* Load More */}
        {filteredNotifications.length > 0 && (
          <div className="text-center mt-8">
            <button className="px-4 lg:px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm lg:text-base">
              Load Older Notifications
            </button>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden">
        <MobileBottomNav />
      </div>
      
      <Footer />
    </div>
  );
};

export default Notifications;